// Support office hours, mirrored from the platform's config('support.office_hours').
//
// Kept as a copy rather than fetched: it is two lines of data that change about
// never, and making a support page's first paint depend on a cross-service call
// to learn its own opening time is a bad trade.
//
// Evaluated in the BROWSER, not on the server. These pages are cached for an
// hour, so a status rendered at build time would claim "open" through the
// night. A client-side check is always right, whatever the cache says.

/** Minutes past midnight, Europe/Amsterdam, keyed by ISO weekday (1 = Monday). */
const HOURS: Record<number, [number, number]> = {
  1: [9 * 60, 17 * 60 + 30],
  2: [9 * 60, 17 * 60 + 30],
  3: [9 * 60, 17 * 60 + 30],
  4: [9 * 60, 17 * 60 + 30],
  5: [9 * 60, 17 * 60],
};

const TIME_ZONE = "Europe/Amsterdam";

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

export type SupportStatus =
  | { open: true }
  | { open: false; opensAt: string; opensOn: "today" | "tomorrow" | number };

/**
 * The visitor's clock is not evidence about our opening time: someone reading
 * this in Sydney is still served from Amsterdam. Intl does the conversion, so
 * this stays correct across both DST switches without a table of dates.
 */
function amsterdamNow(at: Date): { weekday: number; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIME_ZONE,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(at);

  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";

  return {
    weekday: WEEKDAY_INDEX[get("weekday")] ?? 1,
    // "24" is what en-GB emits for midnight under hour12:false.
    minutes: (Number(get("hour")) % 24) * 60 + Number(get("minute")),
  };
}

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/**
 * Whether support is reachable right now, and when it opens next if not.
 *
 * Deliberately not a queue model or a wait estimate. One inbox at roughly
 * thirteen conversations a day cannot support an estimate that is not fiction.
 */
export function supportStatus(at: Date = new Date()): SupportStatus {
  const { weekday, minutes } = amsterdamNow(at);
  const today = HOURS[weekday];

  if (today && minutes >= today[0] && minutes < today[1]) {
    return { open: true };
  }

  // Today still counts when we have not reached opening time yet.
  for (let offset = 0; offset <= 7; offset++) {
    const day = ((weekday - 1 + offset) % 7) + 1;
    const window = HOURS[day];

    if (!window) continue;
    if (offset === 0 && minutes >= window[0]) continue;

    return {
      open: false,
      opensAt: clock(window[0]),
      opensOn: offset === 0 ? "today" : offset === 1 ? "tomorrow" : day,
    };
  }

  // Unreachable while HOURS has any weekday in it, but a status is a promise
  // and a broken table must not turn into a confident one.
  return { open: false, opensAt: clock(HOURS[1][0]), opensOn: "tomorrow" };
}
